import { Injectable } from '@angular/core';
import { CONSTANTS } from '@pk/powerkioskutils';
import { RouterService } from 'src/app/core/router.service';

import { BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';

import moment from 'moment-timezone';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { AppStateService } from '../core/app-state.service';
import { DataLayerService } from '../core/data-layer.service';
import { GraphqlService, GraphqlQueryResult } from '../graphql/graphql.service';
import { AuthFieldsData } from '../graphql/models/responses';
import { AuthFields, User } from '../shared/models';

@Injectable({
	providedIn: 'root',
})
export class SecurityService {
	public isDirect: boolean;
	public authFields: AuthFields;
	public authFieldsSetEvent: Subject<AuthFields> = new Subject<AuthFields>();

	private refreshTokenPromise: Promise<AuthFields>;

	constructor(
		private graphqlService: GraphqlService,
		private routerService: RouterService,
		private toastrService: ToastrService,
		private modalService: BsModalService,
		private appStateService: AppStateService,
		private cookieService: CookieService,
		private dataLayerService: DataLayerService,
	) {}

	public async login(username: string, password: string, rememberMe = false, playLoginSound = false): Promise<GraphqlQueryResult<AuthFieldsData>> {
		const res = await this.graphqlService.authenticateUser(username, password);
		if (res && res.data && res.data.authenticateUser) {
			if (res.data.authenticateUser.loggedInUser.agentId) {
				this.dataLayerService.fireTagEvent({
					event: 'sign-in',
					// eslint-disable-next-line @typescript-eslint/naming-convention
					'agent-id': res.data.authenticateUser.loggedInUser.agentId,
				});
			}
			this.setAuthFields(res.data.authenticateUser.token, res.data.authenticateUser.refreshToken, res.data.authenticateUser.loggedInUser);
			if (rememberMe) {
				localStorage.setItem(CONSTANTS.storageKeys.rememberedUsername, username);
				localStorage.setItem(CONSTANTS.storageKeys.rememberedPassword, password);
			} else {
				localStorage.removeItem(CONSTANTS.storageKeys.rememberedUsername);
				localStorage.removeItem(CONSTANTS.storageKeys.rememberedPassword);
			}
			this.isDirect = false;
		}
		if (playLoginSound) {
			this.playLoginSound();
		}
		return res;
	}

	public resetAuthFields(resetRememberMe = false): void {
		localStorage.removeItem(CONSTANTS.storageKeys.authFields);
		localStorage.removeItem('isImpersonating');
		this.cookieService.delete('pk-user-email-' + environment.name, '/', 'powerkiosk.com');
		this.cookieService.delete('pk-user-password-' + environment.name, '/', 'powerkiosk.com');
		this.authFields = null;
		this.refreshTokenPromise = null;
		if (resetRememberMe) {
			// usually called during impersonation
			localStorage.removeItem(CONSTANTS.storageKeys.rememberedUsername);
			localStorage.removeItem(CONSTANTS.storageKeys.rememberedPassword);
		}
		this.authFieldsSetEvent.next(null);
	}

	public async logout(returnUrl?: string): Promise<boolean> {
		const activeToast = this.toastrService.info('Logging out...');
		const tokens = [];
		const storedAuthFields = localStorage.getItem(CONSTANTS.storageKeys.authFields);
		const authFields = storedAuthFields ? (JSON.parse(storedAuthFields) as AuthFields) : null;
		if (authFields && authFields.token) {
			tokens.push(authFields.token);
		}
		if (authFields && authFields.refreshToken) {
			tokens.push(authFields.refreshToken);
		}
		if (tokens.length) {
			try {
				await this.graphqlService.logoutUser(tokens);
			} catch (e) {
				// token problem, redirect anyway
			}
		}
		this.resetAuthFields();
		this.hideAllModals();
		await this.routerService.navigate(['/security'], { queryParams: { returnUrl } });
		this.toastrService.remove(activeToast.toastId);
		this.appStateService.setReady({ app: false, direct: false, billReader: false, isPopupWindow: false, isEnergyPortal: false });
		return true;
	}

	private hideAllModals(): void {
		// @ts-expect-error: Accessing private variable as workaround for missing feature
		this.modalService.loaders.forEach(loader => {
			loader.instance.hide();
		});
	}

	get isImpersonating(): boolean {
		return !!localStorage.getItem('isImpersonating');
	}

	public setIsImpersonating(): void {
		localStorage.setItem('isImpersonating', '1');
	}

	public async updateAuthFieldsUser(loggedInUser: User): Promise<void> {
		const authFields = await this.getAuthFields();
		authFields.loggedInUser = loggedInUser;
		this.setAuthFields(authFields.token, authFields.refreshToken, authFields.loggedInUser);
	}

	public setAuthFields(token: string, refreshToken: string, user: User | any): void {
		const userSet = typeof user !== 'string' ? JSON.stringify(user) : user;
		this.authFields = new AuthFields(token, refreshToken, JSON.parse(userSet));
		localStorage.setItem(CONSTANTS.storageKeys.authFields, JSON.stringify(this.authFields));
		this.setUserIdCookie();
		this.authFieldsSetEvent.next(this.authFields);
	}

	public setUserIdCookie(): void {
		const password = localStorage.getItem(CONSTANTS.storageKeys.rememberedPassword);

		this.cookieService.set(
			'pk-user-' + environment.name,
			this.authFields.loggedInUser.userId,
			moment().add(1, 'year').toDate(),
			'/',
			'.powerkiosk.com',
		);

		if (password) {
			this.cookieService.set(
				'pk-user-email-' + environment.name,
				this.authFields.loggedInUser.email,
				moment().add(1, 'year').toDate(),
				'/',
				'.powerkiosk.com',
			);
			this.cookieService.set('pk-user-password-' + environment.name, password, moment().add(1, 'year').toDate(), '/', '.powerkiosk.com');
		}
	}

	public getAuthFields(useExisting = false): Promise<AuthFields> {
		const storedAuthFields = localStorage.getItem(CONSTANTS.storageKeys.authFields);
		const parsedAuthFields = storedAuthFields ? JSON.parse(storedAuthFields) : null;
		const authFields = parsedAuthFields ? new AuthFields(parsedAuthFields.token, parsedAuthFields.refreshToken, parsedAuthFields.loggedInUser) : null;

		// useExistingToken is for when doing the actual refresh
		// and direct pages should never use token anyway
		if (!useExisting && !this.isDirect) {
			if (storedAuthFields && authFields.isTokenExpired()) {
				// reuse the promise just in case other requests are
				// also trying to refresh the token
				if (this.refreshTokenPromise) {
					return this.refreshTokenPromise;
				} else {
					// setup the promise first and then return it
					// so that it can be reused above
					this.refreshTokenPromise = this.refreshToken();
					return this.refreshTokenPromise;
				}
			}
		}

		// once we have the token, we can reset the refresh
		this.refreshTokenPromise = null;

		return Promise.resolve(authFields ? authFields : new AuthFields());
	}

	public async refreshToken(): Promise<AuthFields> {
		try {
			const refreshResult = await this.graphqlService.refreshToken();
			this.setAuthFields(
				refreshResult.data.refreshToken.token,
				refreshResult.data.refreshToken.refreshToken,
				refreshResult.data.refreshToken.loggedInUser,
			);
			return this.authFields;
		} catch (e) {
			this.resetAuthFields();
			if (e.message.toLowerCase().includes('unauthorized')) {
				const username = localStorage.getItem(CONSTANTS.storageKeys.rememberedUsername);
				const password = localStorage.getItem(CONSTANTS.storageKeys.rememberedPassword);
				if (username && password) {
					await this.login(username, password, true);
					return this.authFields;
				}
				await this.logout();
			}
			return new AuthFields();
		}
	}

	public async impersonateUser(login: User): Promise<void> {
		try {
			login['impersonationLoading'] = true;
			const res = await this.graphqlService.impersonateUser(login.userId);
			if (res && res.data) {
				this.resetAuthFields(true);
				this.setAuthFields(res.data.impersonateUser.token, res.data.impersonateUser.refreshToken, res.data.impersonateUser.loggedInUser);
				this.setIsImpersonating();
				this.appStateService.onUserUpdated.next(login);
				await this.routerService.navigate(['/']).then(() => {
					window.scroll(0, 0);
				});
			} else {
				this.toastrService.warning('There was a problem impersonating the user.', 'Impersonation');
			}
		} catch (e) {
			this.toastrService.warning('There was a problem impersonating the user.', 'Impersonation');
		}
	}

	public playLoginSound(): void {
		const loginSound = new Audio('https://assets.powerkiosk.com/includes/sounds/login.wav');
		loginSound.play();
	}
}
